import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { blobToBase64 } from '../../../utils/blobToBase64';
import { AIChatContext, LailaAudio } from '../context';
import { messageCards, messageText } from '../utils';
import { useVoiceRecorder } from './useVoiceRecorder';

export type LailaVoicePhase = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';
// Why the last turn went wrong: no microphone, nothing heard, no answer, or no audio.
export type LailaVoiceProblem = 'mic' | 'unheard' | 'failed' | 'mute' | null;

// The recorder's level (0–1) above which someone is talking.
const SPEECH_LEVEL = 0.12;
// This much quiet after speech ends the question.
const END_OF_SPEECH_MS = 800;
// Nothing said this long after tapping the mic: Laila stops listening.
const NO_SPEECH_MS = 8000;
const MAX_QUESTION_MS = 60000;
// OpenAI's speech input limit is 4,096 characters.
const MAX_SPEECH_CHARS = 4000;

// Markdown read aloud says "asterisk asterisk": keep the words, drop the marks.
export const speakable = (text: string) =>
  text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s*\|?\s*:?-{3,}.*$/gm, '')
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+\.)\s+/gm, '')
    .replace(/^[ \t]*\||\|[ \t]*$/gm, '')
    .replace(/[ \t]*\|[ \t]*/g, ', ')
    .replace(/[*_`~]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_SPEECH_CHARS);

// Where a sentence ends: . ! ? or … (with any closing quote or bracket) and a
// space, or a line break. "3.5" and "vyce.io" don't end one.
const SENTENCE_END = /[.!?…]+["')\]]*\s+|\n+/g;
// Shorter sentences ("Sure.") wait for the next, so speech isn't choppy.
const MIN_CHUNK_CHARS = 25;

// The next pieces of a streaming answer ready to be spoken, and how far into
// the text they reach. Mid-answer only whole sentences go; at the end, the rest.
export const speakableChunks = (text: string, final: boolean) => {
  const chunks: string[] = [];
  let start = 0;
  let end = 0;
  let current = '';
  SENTENCE_END.lastIndex = 0;
  for (let match = SENTENCE_END.exec(text); match; match = SENTENCE_END.exec(text)) {
    current += text.slice(start, match.index + match[0].length);
    start = match.index + match[0].length;
    if (current.trim().length >= MIN_CHUNK_CHARS) {
      chunks.push(current.trim());
      current = '';
      end = start;
    }
  }
  if (final) {
    const rest = (current + text.slice(start)).trim();
    if (rest) {
      chunks.push(rest);
    }
    end = text.length;
  }
  return { chunks, end };
};

const base64ToArrayBuffer = (base64: string) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Loudness (0–1) of what an analyser hears, scaled as the recorder's meter.
const loudness = (analyser: AnalyserNode, samples: Uint8Array) => {
  analyser.getByteTimeDomainData(samples);
  let sum = 0;
  samples.forEach(sample => {
    const centred = (sample - 128) / 128;
    sum += centred * centred;
  });
  return Math.min(1, Math.sqrt(sum / samples.length) * 4);
};

// The first piece waits this long, so the next can line up behind it.
const START_LEAD_S = 0.05;

// What is playing: every piece of Laila's voice, back to back, through one meter.
interface Playback {
  sources: Set<AudioBufferSourceNode>;
  analyser: AnalyserNode;
  frame: number;
  // When the last scheduled piece ends, in the audio context's time.
  endsAt: number;
}

// One spoken answer. The server voices it as it writes it and streams the
// voice in pieces, each played as soon as it comes. Only if none of its voice
// arrives is the answer voiced here, as a fallback.
interface Speech {
  ticket: number;
  // Voice came from the server, so none is made here.
  fromServer: boolean;
  // Whole mp3 sentences from the server still being decoded, in order.
  decoding: number;
  decoded: Promise<void>;
  // Fallback: how much of the text is queued, and sentences waiting to be voiced.
  queued: number;
  texts: string[];
  voicing: boolean;
  played: number;
  // The answer is complete, so the queue won't grow.
  complete: boolean;
  // Ends the turn if the last piece never finishes playing.
  watchdog?: number;
}

const newSpeech = (ticket: number): Speech => ({
  ticket,
  fromServer: false,
  decoding: 0,
  decoded: Promise.resolve(),
  queued: 0,
  texts: [],
  voicing: false,
  played: 0,
  complete: false,
});

// 16-bit little-endian mono PCM, base64-encoded, as a playable buffer.
const pcmBuffer = (context: AudioContext, base64: string, sampleRate: number) => {
  const bytes = new DataView(base64ToArrayBuffer(base64));
  const length = Math.floor(bytes.byteLength / 2);
  const buffer = context.createBuffer(1, Math.max(length, 1), sampleRate);
  const channel = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    channel[i] = bytes.getInt16(i * 2, true) / 32768;
  }
  return buffer;
};

// Talking to Laila: tap, ask, and hear the answer. Questions and answers go
// into the same conversation as the chat.
export const useLailaVoice = () => {
  const {
    messages,
    thinking,
    send,
    stop: stopChat,
    retry,
    speechToText,
    textToSpeech,
    setAudioListener,
  } = useContext(AIChatContext);
  const recorder = useVoiceRecorder();
  const [phase, setPhase] = useState<LailaVoicePhase>('idle');
  const [problem, setProblem] = useState<LailaVoiceProblem>(null);
  const [outputLevel, setOutputLevel] = useState(0);
  // Where the current voice turn starts in the conversation: its question, then the answer.
  const [turnStart, setTurnStart] = useState<number | null>(null);

  // Bumped whenever the user moves on, so a late transcript or voice is dropped.
  const run = useRef(0);
  const audio = useRef<AudioContext | null>(null);
  const playback = useRef<Playback | null>(null);
  // Whether the answer was seen on its way; if it stops without one, it was stopped elsewhere.
  const sawThinking = useRef(false);
  const speech = useRef({ heard: false, startedAt: 0, lastHeardAt: 0 });
  const answerSpeech = useRef<Speech>(newSpeech(0));
  const latest = useRef({ messages, level: recorder.level });
  latest.current = { messages, level: recorder.level };

  // Browsers only play sound started by a tap, so the audio context is
  // created and woken in one; later answers can then play on their own.
  const unlockAudio = () => {
    if (!audio.current) {
      const Context = window.AudioContext || (window as any).webkitAudioContext;
      audio.current = Context ? new Context() : null;
    }
    audio.current?.resume().catch(() => {});
  };

  const stopSpeaking = useCallback(() => {
    const current = playback.current;
    if (!current) {
      return;
    }
    playback.current = null;
    cancelAnimationFrame(current.frame);
    current.sources.forEach(source => {
      source.onended = null;
      try {
        source.stop();
      } catch (e) {
        // Already finished.
      }
    });
    setOutputLevel(0);
  }, []);

  // Ends the turn once the answer is complete and all of its voice was played.
  const finishIfDone = (current: Speech) => {
    if (current.ticket !== run.current) {
      return;
    }
    if (!current.complete || current.texts.length || current.voicing || current.decoding) {
      return;
    }
    const playing = playback.current;
    if (playing?.sources.size) {
      // The last piece ending finishes the turn. Should it never play or end,
      // say audio blocked by the browser, this does, once it should have.
      const context = audio.current;
      const left = context?.state === 'running' ? Math.max(0, playing.endsAt - context.currentTime) : 0;
      window.clearTimeout(current.watchdog);
      current.watchdog = window.setTimeout(() => {
        if (current.ticket === run.current && playback.current === playing) {
          if (context?.state !== 'running') {
            setProblem('mute');
          }
          stopSpeaking();
          setPhase('idle');
        }
      }, (left + 1) * 1000);
      return;
    }
    window.clearTimeout(current.watchdog);
    if (!current.played) {
      setProblem('mute');
    }
    stopSpeaking();
    setPhase('idle');
  };

  // Plays a piece of Laila's voice right after the pieces before it.
  const play = (current: Speech, buffer: AudioBuffer) => {
    const context = audio.current;
    if (!context || current.ticket !== run.current) {
      return;
    }
    let playing = playback.current;
    if (!playing) {
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      analyser.connect(context.destination);
      const samples = new Uint8Array(analyser.fftSize);
      const started: Playback = { sources: new Set(), analyser, frame: 0, endsAt: 0 };
      const measure = () => {
        setOutputLevel(loudness(analyser, samples));
        started.frame = requestAnimationFrame(measure);
      };
      started.frame = requestAnimationFrame(measure);
      playback.current = started;
      playing = started;
    }
    const owner = playing;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(owner.analyser);
    const startAt = Math.max(context.currentTime + START_LEAD_S, owner.endsAt);
    owner.endsAt = startAt + buffer.duration;
    owner.sources.add(source);
    source.onended = () => {
      owner.sources.delete(source);
      if (playback.current === owner) {
        finishIfDone(current);
      }
    };
    source.start(startAt);
    current.played++;
    setPhase('speaking');
  };

  // Fallback: voices queued sentences one at a time, in order.
  const voiceNext = async (current: Speech) => {
    if (current.voicing || current.ticket !== run.current) {
      return;
    }
    const text = current.texts.shift();
    if (!text) {
      finishIfDone(current);
      return;
    }
    current.voicing = true;
    const mp3 = await textToSpeech(speakable(text));
    const context = audio.current;
    let buffer: AudioBuffer | null = null;
    try {
      buffer = mp3 && context ? await context.decodeAudioData(base64ToArrayBuffer(mp3)) : null;
    } catch (e) {
      // Skipped below, like a sentence that couldn't be voiced.
    }
    current.voicing = false;
    if (current.ticket !== run.current) {
      return;
    }
    if (buffer) {
      play(current, buffer);
    }
    finishIfDone(current);
    voiceNext(current);
  };

  // Laila's voice from the server: PCM pieces play as they come; whole mp3
  // sentences are decoded first, in order.
  const receiveAudio = useRef<(sentence: LailaAudio) => void>();
  receiveAudio.current = sentence => {
    const current = answerSpeech.current;
    const context = audio.current;
    if (current.ticket !== run.current) {
      return;
    }
    current.fromServer = true;
    if (sentence.pcm) {
      if (context) {
        play(current, pcmBuffer(context, sentence.pcm, sentence.sampleRate || 24000));
      }
      return;
    }
    const mp3 = sentence.mp3;
    if (!mp3) {
      return;
    }
    current.decoding++;
    current.decoded = current.decoded.then(async () => {
      let buffer: AudioBuffer | null = null;
      try {
        buffer = context ? await context.decodeAudioData(base64ToArrayBuffer(mp3)) : null;
      } catch (e) {
        // Skipped, like a sentence that couldn't be voiced.
      }
      current.decoding--;
      if (buffer) {
        play(current, buffer);
      }
      finishIfDone(current);
    });
  };

  // Follows the answer as it is written. The server speaks it; once it is
  // complete without any of her voice having come, it is voiced here instead.
  const speakAnswer = (text: string, complete: boolean) => {
    const current = answerSpeech.current;
    if (current.ticket !== run.current) {
      return;
    }
    if (!complete || current.fromServer) {
      current.complete = complete;
      finishIfDone(current);
      return;
    }
    const { chunks, end } = speakableChunks(text.slice(current.queued), complete);
    current.queued += end;
    current.texts.push(...chunks);
    current.complete = complete;
    voiceNext(current);
  };

  // Sends a question, typed or heard, and waits to speak the answer.
  const ask = (text: string) => {
    unlockAudio();
    stopSpeaking();
    answerSpeech.current = newSpeech(++run.current);
    setProblem(null);
    sawThinking.current = false;
    setTurnStart(latest.current.messages.length);
    setPhase('thinking');
    send(text);
  };

  const listen = async () => {
    unlockAudio();
    stopSpeaking();
    run.current++;
    // Interrupting Laila mid-answer: the chat takes one question at a time.
    if (thinking) {
      stopChat();
    }
    setProblem(null);
    try {
      await recorder.start();
    } catch (e) {
      setProblem('mic');
      setPhase('idle');
      return;
    }
    speech.current = { heard: false, startedAt: Date.now(), lastHeardAt: 0 };
    setPhase('listening');
  };

  // Ends the question: transcribe it and ask.
  const finish = async () => {
    const ticket = ++run.current;
    setPhase('transcribing');
    const blob = await recorder.stop();
    const text = blob ? await speechToText(await blobToBase64(blob)) : '';
    if (ticket !== run.current) {
      return;
    }
    if (!text.trim()) {
      setProblem('unheard');
      setPhase('idle');
      return;
    }
    ask(text);
  };

  const tryAgain = () => {
    unlockAudio();
    answerSpeech.current = newSpeech(++run.current);
    setProblem(null);
    sawThinking.current = false;
    setPhase('thinking');
    retry();
  };

  // Stops whatever is going on: listening, waiting or speaking.
  const stop = () => {
    run.current++;
    stopSpeaking();
    recorder.cancel();
    // Laila may be speaking the start of an answer she is still writing.
    if (thinking) {
      stopChat();
    }
    setPhase('idle');
  };

  // Ends the question when the speaker goes quiet; gives up if nobody speaks.
  useEffect(() => {
    if (phase !== 'listening') {
      return;
    }
    const timer = setInterval(() => {
      const now = Date.now();
      const said = speech.current;
      if (latest.current.level > SPEECH_LEVEL) {
        said.heard = true;
        said.lastHeardAt = now;
      }
      if (
        said.heard &&
        (now - said.lastHeardAt > END_OF_SPEECH_MS || now - said.startedAt > MAX_QUESTION_MS)
      ) {
        clearInterval(timer);
        finish();
      } else if (!said.heard && now - said.startedAt > NO_SPEECH_MS) {
        clearInterval(timer);
        run.current++;
        recorder.cancel();
        setProblem('unheard');
        setPhase('idle');
      }
    }, 100);
    return () => clearInterval(timer);
    // finish and the recorder only change with the render; the phase decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    setAudioListener(sentence => receiveAudio.current?.(sentence));
    return () => setAudioListener(null);
  }, [setAudioListener]);

  // Speaks the answer sentence by sentence as it is written, rather than
  // waiting for all of it: Laila starts talking after her first sentence.
  useEffect(() => {
    if ((phase !== 'thinking' && phase !== 'speaking') || turnStart === null) {
      return;
    }
    if (thinking) {
      sawThinking.current = true;
    }
    const turn = messages.slice(turnStart);
    const answer = turn.find(message => message.role === 'assistant');
    if (answer) {
      speakAnswer(messageText(answer), !thinking);
    } else if (thinking) {
      return;
    } else if (turn[0]?.failed) {
      setProblem('failed');
      setPhase('idle');
    } else if (sawThinking.current) {
      setPhase('idle');
    }
    // speakAnswer only changes with the render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, thinking, messages, turnStart]);

  useEffect(
    () => () => {
      run.current++;
      stopSpeaking();
      audio.current?.close().catch(() => {});
    },
    [stopSpeaking]
  );

  const turn = turnStart === null ? [] : messages.slice(turnStart);
  const question = turn[0]?.role === 'user' ? turn[0] : undefined;
  const answer = question && turn.find(message => message.role === 'assistant');

  return {
    phase,
    problem,
    // How loud the user is while Laila listens, and Laila while she speaks.
    level: phase === 'listening' ? recorder.level : outputLevel,
    seconds: recorder.seconds,
    question: question ? messageText(question) : '',
    answer: answer ? speakable(messageText(answer)) : '',
    // What Laila put on screen with the answer: invites to check and send, and so on.
    cards: answer ? messageCards(answer) : [],
    listen,
    finish,
    ask,
    stop,
    tryAgain,
  };
};
